import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { DisciplineInKid } from '@brighterly/lib-core-types/discipline/interfaces'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useKidStore } from '@stores/kid'
import { useDisciplineStore } from '@stores/discipline'

import { useKidInCustomer } from '@hooks/kid/kid-in-customer.hook'

interface DisciplineInKidReturn {
  isDisciplineMath: ComputedRef<boolean>
  isDisciplineEla: ComputedRef<boolean>
  isMathLanding: ComputedRef<boolean>
  isElaLanding: ComputedRef<boolean>
}

const hasDisciplineAccess = (discipline: DisciplineInKid): boolean => !!discipline.demo?.id || !!discipline.is_paid

export const useDisciplineInKid = (): DisciplineInKidReturn => {
  const kidStore = useKidStore()
  const disciplineStore = useDisciplineStore()

  const {
    kid,
  } = useKidInCustomer({
    kidId: kidStore.activeKidId!,
  })

  const findDiscipline = (code: Discipline): DisciplineInKid | null =>
    kid.value?.disciplines.find((discipline) => discipline.code === code) ?? null

  const mathDiscipline = computed<DisciplineInKid | null>(() => findDiscipline(Discipline.Math))
  const elaDiscipline = computed<DisciplineInKid | null>(() => findDiscipline(Discipline.ELA))

  const isDisciplineMath = computed<boolean>(() => !!mathDiscipline.value)
  const isDisciplineEla = computed<boolean>(() => !!elaDiscipline.value)

  const isMathLanding = computed<boolean>(() =>
    !mathDiscipline.value || disciplineStore.isMathLanding || !hasDisciplineAccess(mathDiscipline.value),
  )

  const isElaLanding = computed<boolean>(() =>
    !elaDiscipline.value || disciplineStore.isElaLanding || !hasDisciplineAccess(elaDiscipline.value),
  )

  return {
    isDisciplineMath,
    isDisciplineEla,
    isMathLanding,
    isElaLanding,
  }
}
